import { SxProps } from '@cp/ds';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { Stack } from '@cp/ds/src/components/stack';
import { Typography } from '@cp/ds/src/components/typography';
import { useDialogViewContext, useOpenDialog } from 'src/features/dialog/view';
import { DealViewModel } from '@common/model/deal';
import { DialogMemberType, MemberUser } from '@youhelp-ru/core';
import { useDealView } from 'src/features/deal';
import { ClickType } from 'src/entities/deal';
import { BotIdentificator } from '@common/model/bot';
import { coreDialogApi } from '@cp/entities/dialog';
import { useToast } from '@cp/shared/toast';
import { TypeOfItems } from '../../lib/config';
import { useProcessingAndReviews } from '../processingAndReviews';
import { useIsLoading } from '../../model/useIsLoading';
import { withRenderProcessingItem } from './processingItem';
import { withRenderReviewItem } from './reviewItem';

export type ClickFeature = (deal?: DealViewModel) => (type: ClickType) => void;

interface ProcessingAndReviewsListProps {
  sx?: SxProps;
}

export const ProcessingAndReviewsList = (props: ProcessingAndReviewsListProps) => {
  const toast = useToast();
  const viewDeal = useDealView();
  const isLoading = useIsLoading();
  const { Viewer } = useDialogViewContext();
  const { data } = useProcessingAndReviews();
  const { openDialog, ...viewerProps } = useOpenDialog();
  const [loadDialog] = coreDialogApi.endpoints.getDialogs.useLazyQuery();

  if (isLoading) {
    return <ContentSkeleton blocks={4} />;
  }

  const handleClick: ClickFeature = (deal) => async (type) => {
    if (!deal) return;
    switch (type) {
      case 'to':
        if (deal.initiatorClientId) {
          openDialog(
            {
              id: deal.dialogId,
            },
            MemberUser.client(deal.initiatorClientId),
          );
        }
        break;
      case 'from':
        if (deal.initiatorClientId) {
          const asId =
            deal.initiatorClientId === deal.subjectConsumerClientId ? deal.subjectProviderClientId : deal.subjectConsumerClientId;
          openDialog(
            {
              id: deal.dialogId,
            },
            MemberUser.client(asId),
          );
        }
        break;
      case 'order':
        viewDeal(deal);
        break;
      case 'support':
        const {
          items: [dialog],
        } = await loadDialog({ as: MemberUser.client(deal.subjectConsumerClientId), pagination: 1 }).unwrap();
        if (!dialog?.members.find((m) => m.type === DialogMemberType.Bot && m.entityId === BotIdentificator.SupportBot)) {
          toast('error', 'Чат с техподдержка не найден');
          return;
        }
        openDialog(
          {
            id: dialog.id,
          },
          MemberUser.bot(BotIdentificator.SupportBot),
        );
        break;
      default:
        return;
    }
  };

  return (
    <Stack spacing={1.5} sx={props.sx}>
      {data.typeOfItems === TypeOfItems.Processing && data.items.map(withRenderProcessingItem(handleClick))}
      {data.typeOfItems === TypeOfItems.Review && data.items.map(withRenderReviewItem(handleClick))}
      {!data.items.length && !isLoading && (
        <Typography sx={{ opacity: 0.35 }} variant="body bold">
          Ничего не найдено...
        </Typography>
      )}
      <Viewer {...viewerProps} />
    </Stack>
  );
};
